import { defineStore } from 'pinia'

import { getBranch, getBranchHeadcount } from '@/service/branch'
import { useAppStore } from '@/store/app'

import type { BranchArrivalTimeInfo, BranchData, EatigoBranchHeadcountInfo, FbranchEatigoListData } from '@/types/api'

type ListKey = 'popularProducts' | 'discountProducts' | 'searchProduct' | 'branchRelatedProduct' | 'branchHotProduct' | 'collections'

export const useBranchStore = defineStore('branch', () => {
  const appStore = useAppStore()
  const currentBranch = ref<Nullable<BranchData>>(null)
  const arrivalTimes = ref<Nullable<BranchArrivalTimeInfo>>(null)
  const headcountInfo = ref<Nullable<EatigoBranchHeadcountInfo>>(null)
  const hasGoogleAiSummary = ref(false)
  const hasFunNowAiSummary = ref(false)

  const branchListItemMap = reactive<Record<number, FbranchEatigoListData>>({})
  const listIds = reactive<Partial<Record<ListKey, number[]>>>({})

  const currentBranchId = computed(() => currentBranch.value?.bid)
  const saleRegionIds = computed(() => currentBranch.value?.sale_region_ids || [])

  const setArrivalTimes = (data: BranchArrivalTimeInfo) => {
    arrivalTimes.value = data
  }

  const setBranchListItemMap = (branch: FbranchEatigoListData) => {
    if (branch.bid) {
      branchListItemMap[branch.bid] = branch
    }
  }

  const setBranches = (key: ListKey, branches?: Nullable<BranchData[]>) => {
    if (!Array.isArray(branches)) {
      return
    }

    listIds[key] = branches.map(branch => branch.bid!)
    branches.forEach(branch => {
      setBranchListItemMap(branch)
    })
  }

  const fetchBranch = async (bid: string) => {
    if (!bid) return null
    if (import.meta.server && currentBranchId.value === Number(bid)) {
      return currentBranch.value
    }
    const params = {
      ...appStore.geolocation,
    }
    const { data } = await getBranch(bid, params)

    currentBranch.value = data

    return data
  }

  const fetchHeadcount = async (bid: string, pid: number) => {
    const queryParams = { pid }
    const { data } = await getBranchHeadcount(bid, queryParams)

    headcountInfo.value = data

    return data
  }

  return {
    arrivalTimes,
    headcountInfo,
    branchListItemMap,
    listIds,
    currentBranchId,
    saleRegionIds,
    hasGoogleAiSummary,
    hasFunNowAiSummary,
    setArrivalTimes,
    setBranchListItemMap,
    setBranches,
    fetchBranch,
    fetchHeadcount,
  }
})
